// src/components/merchant/DealCitiesPicker.js
// ============================================================
// VILLES OÙ CETTE ANNONCE EST DISPONIBLE
// ------------------------------------------------------------
// Le marchand saisit ses lieux UNE FOIS dans son profil
// (Paramètres → Lieux de prestation), avec pour chacun sa ville,
// son adresse, ses horaires et ses consignes.
//
// Ici, sur CHAQUE annonce, on coche simplement les lieux
// concernés. Une annonce peut donc être disponible à Tanger et
// Tétouan sans qu'on ait à la créer deux fois.
//
// Ce que ça change ensuite : à l'achat, le client choisit sa
// ville parmi celles cochées, et son coupon imprime l'adresse
// et les horaires de CE lieu-là.
//
// UN LIEU SANS VILLE N'EST PAS PROPOSÉ. Il ne pourrait ni
// remonter dans une recherche par ville, ni être choisi par le
// client. On l'affiche quand même, grisé, avec le lien pour
// aller le compléter — sinon le marchand chercherait pourquoi
// son lieu n'apparaît pas.
//
// Maxula — AbodyStudio Limited
// ============================================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMapPin, FiAlertTriangle, FiSettings } from 'react-icons/fi';
import { merchantAPI } from '../../services/api';

export default function DealCitiesPicker({ value = [], onChange, disabled = false }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const r = await merchantAPI.getProfile();
        const m = r?.data?.data || r?.data || {};
        let l = m.serviceLocations || [];
        if (typeof l === 'string') { try { l = JSON.parse(l); } catch (e) { l = []; } }
        if (vivant) setLocations(Array.isArray(l) ? l : []);
      } catch (e) {
        // Chargement facultatif : l'annonce reste enregistrable sans.
      } finally {
        if (vivant) setLoading(false);
      }
    })();
    return () => { vivant = false; };
  }, []);

  const selected = Array.isArray(value) ? value.map(String) : [];

  const toggle = (id) => {
    const s = String(id);
    const next = selected.includes(s)
      ? selected.filter((x) => x !== s)
      : [...selected, s];
    onChange && onChange(next);
  };

  const avecVille = locations.filter((l) => l && l.cityId);
  const sansVille = locations.filter((l) => l && !l.cityId);

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-semibold flex items-center gap-2">
          <FiMapPin className="text-[#e11d7b]" size={16} />
          Villes où cette annonce est disponible
        </div>
        <Link
          to="/merchant/settings"
          className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
        >
          <FiSettings size={12} /> Gérer mes lieux
        </Link>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Cochez les lieux concernés. Le client choisira sa ville à l'achat et son
        coupon affichera l'adresse et les horaires de ce lieu.
      </p>

      {loading ? (
        <div className="text-sm text-gray-500 py-2">Chargement de vos lieux…</div>
      ) : avecVille.length === 0 && sansVille.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Aucun lieu enregistré. Ajoutez vos adresses dans
          {' '}<Link to="/merchant/settings" className="underline font-semibold">Paramètres → Lieux de prestation</Link>,
          puis revenez cocher les villes.
        </div>
      ) : (
        <div className="space-y-2">
          {avecVille.map((l) => {
            const coche = selected.includes(String(l.id));
            return (
              <label
                key={l.id}
                className={`flex items-start gap-3 border rounded-lg px-3 py-2 cursor-pointer transition ${
                  coche ? 'border-[#e11d7b] bg-[#fff5fa]' : 'border-gray-200 hover:border-gray-300'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={coche}
                  disabled={disabled}
                  onChange={() => toggle(l.id)}
                  className="mt-1"
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {l.cityName || 'Ville'} <span className="font-normal text-gray-500">· {l.label}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{l.addressFull}</div>
                  {l.openingHours ? (
                    <div className="text-xs text-gray-400">{l.openingHours}</div>
                  ) : null}
                </div>
              </label>
            );
          })}

          {/* Lieux inutilisables, montres pour que le marchand comprenne
              pourquoi ils n'apparaissent pas dans la liste ci-dessus. */}
          {sansVille.map((l) => (
            <div key={l.id} className="flex items-start gap-3 border border-dashed border-amber-300 bg-amber-50/50 rounded-lg px-3 py-2">
              <FiAlertTriangle className="text-amber-500 mt-1 shrink-0" size={15} />
              <div className="min-w-0 text-sm">
                <div className="font-semibold text-amber-900">{l.label}</div>
                <div className="text-xs text-amber-800">
                  Ville non renseignée — ce lieu ne peut pas être proposé.
                  {' '}<Link to="/merchant/settings" className="underline font-semibold">Le compléter</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected.length > 1 && (
        <p className="text-xs text-green-700 font-semibold mt-3">
          Une seule annonce pour {selected.length} villes — plus besoin de la
          dupliquer.
        </p>
      )}
    </div>
  );
}
